// AdminDashboard.jsx - Admin panel for user management

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.listUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await ApiService.updateUserRole(userId, newRole);
      showSuccess(`User role updated to ${newRole}`);
      setEditingRole(null);
      fetchUsers();
    } catch (err) {
      setError('Failed to update role: ' + err.message);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      // Use updateUserRole as a proxy for status update if no dedicated endpoint
      // For now, we'll just update locally and show a message
      showSuccess(`Status update not yet implemented via API`);
      setEditingStatus(null);
      fetchUsers();
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await ApiService.deleteUser(userId);
      showSuccess('User deleted successfully');
      setConfirmDelete(null);
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'badge badge-admin';
      case 'manager': return 'badge badge-manager';
      case 'worker': return 'badge badge-worker';
      default: return 'badge';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'badge badge-active';
      case 'inactive': return 'badge badge-inactive';
      case 'suspended': return 'badge badge-suspended';
      default: return 'badge';
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h2>Admin Dashboard</h2>
        </div>
        <div className="loading-spinner">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <div className="admin-header-actions">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            &larr; Dashboard
          </button>
          <button className="btn-refresh" onClick={fetchUsers}>
            &#x21bb; Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button className="alert-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {successMsg && (
        <div className="alert alert-success">
          {successMsg}
          <button className="alert-close" onClick={() => setSuccessMsg(null)}>&times;</button>
        </div>
      )}

      <div className="user-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">No users found</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}
                  </td>
                  <td>
                    {editingRole === user.id ? (
                      <div className="inline-edit">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                        >
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="worker">Worker</option>
                        </select>
                        <button
                          className="btn-sm btn-save"
                          onClick={() => handleRoleChange(user.id, selectedRole)}
                        >
                          Save
                        </button>
                        <button
                          className="btn-sm btn-cancel"
                          onClick={() => setEditingRole(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span
                        className={getRoleBadgeClass(user.role)}
                        onClick={() => {
                          setEditingRole(user.id);
                          setSelectedRole(user.role);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(user.status)}>
                      {user.status || 'active'}
                    </span>
                  </td>
                  <td className="date-cell">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-sm btn-danger"
                        onClick={() => setConfirmDelete(user.id)}
                        title="Delete user"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteUser(confirmDelete)}
              >
                Delete
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;